import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FolderCode, Rocket, Boxes, Hammer, PlusCircle, ArrowRight, Clock } from "lucide-react";

const STAT_META = [
  { key: "projects", label: "Projects", icon: FolderCode, color: "text-emerald-400" },
  { key: "builds", label: "Builds", icon: Hammer, color: "text-cyan-400" },
  { key: "deployments", label: "Deployments", icon: Rocket, color: "text-indigo-400" },
  { key: "integrations", label: "Integrations", icon: Boxes, color: "text-amber-400" },
];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold">Welcome back, {user?.name?.split(" ")[0]}</h1>
          <p className="text-slate-400 mt-1">Let your agents ship something today.</p>
        </div>
        <button data-testid="home-new-project" onClick={() => navigate("/dashboard/new")}
          className="h-11 px-5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-slate-950 font-semibold flex items-center gap-2 hover:opacity-90 transition-opacity">
          <PlusCircle className="h-4 w-4" /> New Project
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_META.map((s) => (
          <div key={s.key} className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
            <s.icon className={`h-5 w-5 ${s.color} mb-3`} />
            <div className="text-3xl font-head font-bold" data-testid={`stat-${s.key}`}>{stats[s.key] ?? 0}</div>
            <div className="text-sm text-slate-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-head font-semibold">Recent projects</h2>
          <button onClick={() => navigate("/dashboard/projects")} className="text-sm text-emerald-400 hover:underline flex items-center gap-1" data-testid="view-all-projects">
            View all <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        {projects.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-800 p-12 text-center">
            <FolderCode className="h-8 w-8 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 mb-4">No projects yet. Describe an app and let the agents build it.</p>
            <button onClick={() => navigate("/dashboard/new")} className="text-emerald-400 hover:underline" data-testid="empty-new-project">Create your first project</button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <button key={p.id} data-testid={`recent-project-${p.id}`} onClick={() => navigate(`/workspace/${p.id}`)}
                className="text-left rounded-2xl border border-slate-800 bg-slate-900/40 p-5 hover:border-emerald-500/40 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-head font-semibold truncate">{p.name}</span>
                  <StatusPill status={p.status} />
                </div>
                <p className="text-sm text-slate-400 line-clamp-2 h-10">{p.description || "No description"}</p>
                <div className="flex items-center gap-1 text-xs text-slate-500 mt-3"><Clock className="h-3 w-3" /> {p.file_count} files</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    ready: "bg-emerald-500/15 text-emerald-400", building: "bg-amber-500/15 text-amber-400",
    error: "bg-rose-500/15 text-rose-400", draft: "bg-slate-500/15 text-slate-400",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${map[status] || map.draft}`}>{status}</span>;
}
